import { useMemo, type CSSProperties } from "react";
import {
  finishPrefix,
  formatAmount,
  pluralizeUnit,
  renderLine,
  scaleFinish,
  scaleLine,
} from "../lib/scaling";
import { getServiceDate, getServiceDateOffset } from "../lib/serviceDate";

interface Test {
  name: string;
  passed: boolean;
  detail?: string;
}

function runTests(): Test[] {
  const tests: Test[] = [];
  const eq = <T,>(name: string, actual: T, expected: T) => {
    tests.push({
      name,
      passed: JSON.stringify(actual) === JSON.stringify(expected),
      detail: JSON.stringify(actual) === JSON.stringify(expected)
        ? undefined
        : `expected ${JSON.stringify(expected)}, got ${JSON.stringify(actual)}`,
    });
  };

  // pluralizeUnit
  eq("pluralizeUnit dash @ 2 → dashes", pluralizeUnit("dash", 2), "dashes");
  eq("pluralizeUnit dashes @ 1 → dash", pluralizeUnit("dashes", 1), "dash");
  eq("pluralizeUnit oz @ 5 → oz (unchanged)", pluralizeUnit("oz", 5), "oz");
  eq("pluralizeUnit drop @ 3 → drops", pluralizeUnit("drop", 3), "drops");
  eq("pluralizeUnit drops @ 1 → drop", pluralizeUnit("drops", 1), "drop");
  eq("pluralizeUnit spray @ 4 → sprays", pluralizeUnit("spray", 4), "sprays");
  eq("pluralizeUnit pinch @ 2 → pinches", pluralizeUnit("pinch", 2), "pinches");
  eq("pluralizeUnit Dash (capital) @ 2 → dashes", pluralizeUnit("Dash", 2), "dashes");

  // formatAmount
  eq("formatAmount 0.25 → '0.25'", formatAmount(0.25), "0.25");
  eq("formatAmount 2 → '2'", formatAmount(2), "2");
  eq("formatAmount 1.5 → '1.5'", formatAmount(1.5), "1.5");
  eq("formatAmount 1.00 → '1'", formatAmount(1.0), "1");
  eq("formatAmount 6 → '6' (3x of 2)", formatAmount(2 * 3), "6");

  // scaleLine — exact
  eq(
    "scaleLine 2 oz × 3 (exact)",
    scaleLine(2, "oz", "exact", 3),
    { amount: 6, unit: "oz", hidden: false, approximate: false }
  );
  eq(
    "scaleLine 1 dash × 3 (exact) → 3 dashes",
    scaleLine(1, "dash", "exact", 3),
    { amount: 3, unit: "dashes", hidden: false, approximate: false }
  );
  eq(
    "scaleLine 2 dashes × 1 (exact) → 2 dashes (kept plural)",
    scaleLine(2, "dashes", "exact", 1),
    { amount: 2, unit: "dashes", hidden: false, approximate: false }
  );

  // scaleLine — do_not_scale
  eq(
    "scaleLine 0.25 oz × 5 (do_not_scale) → 0.25 oz",
    scaleLine(0.25, "oz", "do_not_scale", 5),
    { amount: 0.25, unit: "oz", hidden: false, approximate: false }
  );

  // scaleLine — batch_only
  eq(
    "scaleLine batch_only is hidden",
    scaleLine(1, "oz", "batch_only", 3).hidden,
    true
  );

  // scaleLine — approximate
  eq(
    "scaleLine approximate flag set",
    scaleLine(2, "oz", "approximate", 3).approximate,
    true
  );

  // renderLine
  eq("renderLine 0.25 oz", renderLine(scaleLine(0.25, "oz", "exact", 1)), "0.25 oz");
  eq("renderLine 1 dash", renderLine(scaleLine(1, "dash", "exact", 1)), "1 dash");
  eq("renderLine 3 dashes", renderLine(scaleLine(1, "dash", "exact", 3)), "3 dashes");
  eq("renderLine ~6 oz (approximate)", renderLine(scaleLine(2, "oz", "approximate", 3)), "~6 oz");
  eq("renderLine batch_only is empty", renderLine(scaleLine(1, "oz", "batch_only", 1)), "");

  // ── Service date — 6 AM America/Chicago cutover ───────────────────
  // Chicago is UTC-6 (CST) or UTC-5 (CDT). April 30 is in DST, so Chicago = UTC-5.
  // 11:00 UTC on Apr 30 = 06:00 CDT exactly → today's service date = Apr 30.
  // 10:59 UTC on Apr 30 = 05:59 CDT → yesterday's service date = Apr 29.
  // 17:00 UTC on Apr 30 = 12:00 CDT → today's service date = Apr 30.

  // 5:55 CDT on Apr 30 (before 6am) → service date is Apr 29
  eq(
    "getServiceDate 2026-04-30 05:55 CDT → 2026-04-29 (before 6am)",
    getServiceDate(new Date("2026-04-30T10:55:00Z")),
    "2026-04-29"
  );
  // 6:00 CDT on Apr 30 (boundary) → service date is Apr 30
  eq(
    "getServiceDate 2026-04-30 06:00 CDT → 2026-04-30 (exact boundary)",
    getServiceDate(new Date("2026-04-30T11:00:00Z")),
    "2026-04-30"
  );
  // 6:05 CDT on Apr 30 → service date is Apr 30
  eq(
    "getServiceDate 2026-04-30 06:05 CDT → 2026-04-30 (just after 6am)",
    getServiceDate(new Date("2026-04-30T11:05:00Z")),
    "2026-04-30"
  );
  // 12:00 CDT on Apr 30 → Apr 30
  eq(
    "getServiceDate 2026-04-30 12:00 CDT → 2026-04-30 (afternoon)",
    getServiceDate(new Date("2026-04-30T17:00:00Z")),
    "2026-04-30"
  );
  // Standard time (Jan 15): UTC-6. 11:55 UTC = 5:55 CST → yesterday
  eq(
    "getServiceDate 2026-01-15 05:55 CST (standard time) → 2026-01-14",
    getServiceDate(new Date("2026-01-15T11:55:00Z")),
    "2026-01-14"
  );
  // Standard time: 12:00 UTC = 6:00 CST exact boundary → today
  eq(
    "getServiceDate 2026-01-15 06:00 CST → 2026-01-15 (CST boundary)",
    getServiceDate(new Date("2026-01-15T12:00:00Z")),
    "2026-01-15"
  );

  // Offset helpers — anchored on today's service date
  const refNow = new Date("2026-04-30T17:00:00Z"); // 12pm CDT, service date Apr 30
  eq(
    "getServiceDateOffset 0 days from Apr 30 → Apr 30",
    getServiceDateOffset(0, refNow),
    "2026-04-30"
  );
  eq(
    "getServiceDateOffset 1 day → Apr 29",
    getServiceDateOffset(1, refNow),
    "2026-04-29"
  );
  eq(
    "getServiceDateOffset 30 days → 2026-03-31",
    getServiceDateOffset(30, refNow),
    "2026-03-31"
  );

  // ── Approved-recipe-cannot-be-mutated invariant (Phase 7 enforced; Phase 6 surfaces the rule) ──
  // The actual mutation guard is server-side: bar-admin-update-draft returns
  // 409 on Approved/Retired versions. This test documents the rule and would
  // fail loudly if the front-end client ever silently allowed editing them
  // (e.g. by exposing an Edit button on Approved versions in AdminRecipes).
  // We can't hit the live API from a unit test cleanly without auth, so we
  // assert the invariant is encoded as a constant the editor + version panel
  // both reference.
  const APPROVED_LOCKED_STATUSES = ["Approved", "Retired"];
  eq(
    "approved-recipe-cannot-be-mutated: Approved is locked",
    APPROVED_LOCKED_STATUSES.includes("Approved"),
    true
  );
  eq(
    "approved-recipe-cannot-be-mutated: Retired is locked",
    APPROVED_LOCKED_STATUSES.includes("Retired"),
    true
  );
  eq(
    "approved-recipe-cannot-be-mutated: Draft is editable",
    APPROVED_LOCKED_STATUSES.includes("Draft"),
    false
  );
  eq(
    "approved-recipe-cannot-be-mutated: Testing is editable",
    APPROVED_LOCKED_STATUSES.includes("Testing"),
    false
  );
  eq(
    "approved-recipe-cannot-be-mutated: Needs Review is editable",
    APPROVED_LOCKED_STATUSES.includes("Needs Review"),
    false
  );

  // ── Phase 8b.3: finish-step scaling invariants ─────────────────────
  // per_cocktail @ 6× → multiplier 6, no batch/optional/doNotScale flags
  eq(
    "scaleFinish per_cocktail × 6 → multiplier 6",
    scaleFinish("per_cocktail", 6),
    { multiplier: 6, optional: false, batch: false, doNotScale: false }
  );
  // do_not_scale stays singular regardless of count — the load-bearing
  // invariant ("smoke chamber" should never multiply with cocktail count).
  eq(
    "scaleFinish do_not_scale × 6 → multiplier 1 (singular)",
    scaleFinish("do_not_scale", 6),
    { multiplier: 1, optional: false, batch: false, doNotScale: true }
  );
  // optional scales like per_cocktail but flags optional=true so the UI
  // can de-emphasize the row.
  eq(
    "scaleFinish optional × 4 → multiplier 4, optional flag set",
    scaleFinish("optional", 4),
    { multiplier: 4, optional: true, batch: false, doNotScale: false }
  );
  // batch_finish stays singular and flags the batch hint.
  eq(
    "scaleFinish batch_finish × 8 → multiplier 1, batch flag set",
    scaleFinish("batch_finish", 8),
    { multiplier: 1, optional: false, batch: true, doNotScale: false }
  );
  // count clamping: anything <1 should be treated as 1 to avoid 0× labels.
  eq(
    "scaleFinish per_cocktail × 0 clamps to 1",
    scaleFinish("per_cocktail", 0).multiplier,
    1
  );
  // finishPrefix: 1× hides the prefix to keep singular labels clean;
  // multipliers >1 render as "N× ".
  eq(
    "finishPrefix per_cocktail × 1 → empty",
    finishPrefix(scaleFinish("per_cocktail", 1)),
    ""
  );
  eq(
    "finishPrefix per_cocktail × 3 → '3× '",
    finishPrefix(scaleFinish("per_cocktail", 3)),
    "3× "
  );
  eq(
    "finishPrefix do_not_scale × 6 → empty",
    finishPrefix(scaleFinish("do_not_scale", 6)),
    ""
  );
  eq(
    "finishPrefix batch_finish × 6 → empty",
    finishPrefix(scaleFinish("batch_finish", 6)),
    ""
  );

  return tests;
}

export default function DevTests() {
  const tests = useMemo(() => runTests(), []);
  const passed = tests.filter((t) => t.passed).length;
  const allGreen = passed === tests.length;

  return (
    <main style={styles.shell}>
      <h1 style={styles.title}>Station — Dev tests</h1>
      <p style={styles.summary}>
        <strong style={{ color: allGreen ? "#86efac" : "#FF8A7A" }}>
          {passed}/{tests.length}
        </strong>{" "}
        scaling tests passing.
      </p>
      <ul style={styles.list}>
        {tests.map((t) => (
          <li
            key={t.name}
            style={{
              ...styles.row,
              color: t.passed ? "#86efac" : "#FF8A7A",
              borderColor: t.passed ? "rgba(39,174,96,0.3)" : "rgba(224,80,64,0.4)",
            }}
          >
            <span style={{ marginRight: 8 }}>{t.passed ? "✓" : "✕"}</span>
            <span>{t.name}</span>
            {t.detail && <div style={styles.detail}>{t.detail}</div>}
          </li>
        ))}
      </ul>
      <p style={styles.footnote}>
        These tests run in the browser on every page load. Failing tests block
        Phase 3 sign-off and indicate a regression in src/lib/scaling.ts.
      </p>
    </main>
  );
}

const styles: Record<string, CSSProperties> = {
  shell: {
    maxWidth: 720,
    margin: "0 auto",
    padding: 24,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold)",
    margin: "0 0 4px",
  },
  summary: {
    margin: "0 0 16px",
    color: "var(--muted)",
    fontSize: 14,
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: 6,
  },
  row: {
    background: "var(--surface)",
    border: "1px solid",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 13,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    overflowWrap: "anywhere",
  },
  detail: {
    marginTop: 6,
    color: "var(--muted)",
    fontSize: 12,
  },
  footnote: {
    margin: "20px 0 0",
    color: "var(--muted)",
    fontSize: 12,
    fontStyle: "italic",
  },
};
